import React, { useRef, useState } from "react";
import { CSSTransition } from "react-transition-group";

import { colors, easings } from "@apps/viewer/src/ts/config";

import {
  useAudio,
  useLoaderState,
  useSelf,
} from "@apps/viewer/src/ts/components/utils/AudioPlayer/hooks";

const TOGGLE_ICON_TRANSITION_MS = 150;

type Props = {
  src: string;

  /**
   * triggered once the audio file has finished playing
   */
  onEnd?(): void;
};

type Self = {
  isScrubbing?: boolean;
  wasPlayingBeforeScrubbing?: boolean;
};

/**
 * an audio player interface which will float nicely below the top bar, allowing
 * users to play associated audio files.
 */
// tslint:disable-next-line max-func-body-length function-name
export function AudioPlayer(props: Props): React.JSX.Element {
  const audio = useAudio(props);
  const self = useSelf<Self>({});
  const [scrubToMs, setScrubToMs] = useState<number | undefined>(undefined);
  const showLoader = useLoaderState(audio.timeElapsedMs === undefined, {
    allowInitial: true,
    durationMs: 350,
  });

  const handleToggleClick = (): void => {
    if (audio.isPlaying) {
      audio.pause();
    } else {
      audio.resume();
    }
  };

  const handleProgressPointerMove = (
    evt: React.PointerEvent<HTMLDivElement>,
    options: {
      avoidPause?: boolean;
    } = {}
  ): void => {
    if (!self.isScrubbing || !audio.durationMs) {
      return;
    }

    const bbox = evt.currentTarget.getBoundingClientRect();

    setScrubToMs(((evt.pageX - bbox.left) / bbox.width) * audio.durationMs);

    if (!options.avoidPause) {
      audio.pause();
    }
  };

  const handleProgressPointerDown = (
    evt: React.PointerEvent<HTMLDivElement>
  ): void => {
    if (!evt.isPrimary) {
      return;
    }

    self.isScrubbing = true;
    self.wasPlayingBeforeScrubbing = audio.isPlaying;

    handleProgressPointerMove(evt, { avoidPause: true });
  };

  const handleProgressPointerUp = (): void => {
    if (scrubToMs !== undefined) {
      audio.seek(scrubToMs / 1000);
    }

    if (self.wasPlayingBeforeScrubbing) {
      audio.resume();
    }

    self.isScrubbing = false;
    setScrubToMs(undefined);
  };

  const elapsedTimeMs = scrubToMs ?? audio.timeElapsedMs;
  const isPlaying = self.isScrubbing
    ? self.wasPlayingBeforeScrubbing ?? false
    : audio.isPlaying;

  const showPauseButton = !showLoader.current && isPlaying;
  const showPlayButton = !showLoader.current && !isPlaying;

  const throbberRef = useRef<HTMLDivElement>(null);
  const pauseRef = useRef<HTMLDivElement>(null);
  const playRef = useRef<HTMLDivElement>(null);
  const ref = useRef<HTMLDivElement>(null);

  return (
    <div className="placeholder">
      <div className="wrapper">
        <div className="container">
          <button className="toggle" onClick={handleToggleClick}>
            <CSSTransition
              in={showLoader.current}
              timeout={TOGGLE_ICON_TRANSITION_MS}
              classNames="toggleIcon"
              mountOnEnter
              unmountOnExit
              nodeRef={throbberRef}
            >
              <div ref={throbberRef} className="toggleThrobber" />
            </CSSTransition>

            <CSSTransition
              in={showPauseButton}
              timeout={TOGGLE_ICON_TRANSITION_MS}
              classNames="toggleIcon"
              mountOnEnter
              unmountOnExit
              nodeRef={pauseRef}
            >
              <div ref={pauseRef}>
                <svg
                  viewBox="0 0 15 18"
                  xmlns="https://www.w3.org/2000/svg"
                  height={22}
                  className="toggleIcon"
                  fill="none"
                  stroke="currentColor"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="1.5"
                >
                  <rect width="4.5" height="16.5" x=".75" y=".75" rx="1.5" />
                  <rect width="4.5" height="16.5" x="9.75" y=".75" rx="1.5" />
                </svg>
              </div>
            </CSSTransition>

            <CSSTransition
              in={showPlayButton}
              timeout={TOGGLE_ICON_TRANSITION_MS}
              mountOnEnter
              unmountOnExit
              nodeRef={playRef}
            >
              <div ref={playRef}>
                <svg
                  viewBox="0 0 24 24"
                  xmlns="https://www.w3.org/2000/svg"
                  strokeWidth="2"
                  className="toggleIcon"
                  height={22}
                  style={{
                    marginLeft: "1px",
                  }}
                  fill="none"
                  stroke="currentColor"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  {/* tslint:disable-next-line max-line-length */}
                  <path d="M2.338 3.255v17.49a1.5 1.5 0 002.209 1.322l16.323-8.745a1.5 1.5 0 000-2.644L4.547 1.933a1.5 1.5 0 00-2.209 1.322z" />
                </svg>
              </div>
            </CSSTransition>
          </button>

          <div
            className="progressbar"
            onPointerDown={handleProgressPointerDown}
            onPointerMove={handleProgressPointerMove}
            onPointerUp={handleProgressPointerUp}
          >
            <div
              style={{
                width: `${
                  ((elapsedTimeMs ?? 0) / (audio.durationMs ?? 1)) * 100
                }%`,
              }}
            />
          </div>

          <span className="timestamp">
            <span className="timestamp__elapsed">
              {formatTime(elapsedTimeMs)}
            </span>
            &nbsp;&nbsp;|&nbsp;&nbsp;
            <span className="timestamp__duration">
              {formatTime(audio.durationMs)}
            </span>
          </span>
        </div>
      </div>

      <style jsx>{`
        .placeholder {
          height: 51px;
        }

        .wrapper {
          border-bottom: 1px solid rgba(235, 235, 235, 0.96);
          box-sizing: border-box;
          margin: 0 -60px 60px;
          max-width: calc(100vw - 60px);
          padding: 0 60px;
          position: fixed;
          top: 60px;
          width: 1120px;
          z-index: 99;
          background-color: rgba(255, 255, 255, 0.96);

          @media screen and (max-width: 767px) {
            margin: 0 -15px 60px;
            max-width: calc(100vw - 30px);
            padding: 0 15px;
          }
        }

        /**
         * [HARDCODED] fixate position of the audio player during block
         * transition, because otherwise it would jump unwantedly as the
         * css transform created a new composite layer and thus pushing the
         * bounding box of the position: fixed used above
         */
        :global(.blockTransition-enter) .wrapper {
          position: absolute;
          top: -57px;

          @media screen and (max-width: 767px) {
            top: -30px;
          }
        }

        .container {
          align-items: center;
          display: flex;
          height: 50px;
          margin: 0 auto;
          max-width: 40em;
        }

        .toggle {
          appearance: none;
          background: none;
          border: none;
          color: ${colors.black};
          cursor: pointer;
          flex: 0;
          height: 22px;
          margin: -10px 20px -10px 0;
          outline: none;
          padding: 10px;
          position: relative;
          transition: color 0.1s linear;
          width: 20px;

          &:hover {
            color: ${colors.orange};
          }
        }

        .toggleIcon {
          left: 50%;
          position: absolute;
          top: 50%;
          transform: translate(-50%, -50%);
        }

        .toggleIcon-enter {
          opacity: 0.001;
          transform: translate(-50%, -50%) scale(0.2);
        }

        .toggleIcon-enter-active {
          opacity: 1;
          transform: translate(-50%, -50%);
          transition: opacity ${TOGGLE_ICON_TRANSITION_MS}ms linear,
            transform ${TOGGLE_ICON_TRANSITION_MS}ms ${easings.inOutQuad};
        }

        .toggleIcon-exit {
          opacity: 1;
          transform: translate(-50%, -50%);
        }

        .toggleIcon-exit-active {
          opacity: 0.001;
          transform: translate(-50%, -50%) scale(0.2);
          transition: opacity ${TOGGLE_ICON_TRANSITION_MS}ms linear,
            transform ${TOGGLE_ICON_TRANSITION_MS}ms ${easings.inOutQuad};
        }

        .toggleThrobber {
          position: absolute;
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%);
          width: 22px;
          height: 22px;
          cursor: default;

          &::before {
            content: "";
            position: absolute;
            top: 0;
            bottom: 0;
            left: 0;
            right: 0;
            border: 2px solid #dedede;
            border-top-color: ${colors.black};
            border-radius: 50%;
            animation: spin 900ms linear infinite;
          }

          @keyframes spin {
            from {
              transform: rotate(0deg);
            }

            to {
              transform: rotate(360deg);
            }
          }
        }

        .progressbar {
          flex: 1;
          position: relative;
          height: 30px;
          cursor: pointer;

          /** render progressbar using pseudo-elements */
          &::before,
          & > div {
            content: "";
            position: absolute;
            top: calc(50% - 2px);
            left: 0;
            right: 0;
            height: 4px;
            border-radius: 2px;
            background: #dedede;
          }

          & > div {
            background: ${colors.orange};
          }
        }

        .timestamp {
          display: block;
          margin-left: 30px;
          font-family: ${colors.orange};
          font-size: 14px;
          color: ${colors.black};
        }

        .timestamp__elapsed {
          font-weight: bold;
        }

        .timestamp__duration {
          opacity: 0.6;
        }
      `}</style>
    </div>
  );
}

/**
 * transform a time given in milliseconds into a human readable mm:ss format
 */
function formatTime(timeMs: number | undefined): string {
  if (timeMs === undefined) {
    return "--:--";
  }

  const timeSeconds = Math.round(timeMs / 1000);

  const formatedMinutes = String(Math.floor(timeSeconds / 60)).padStart(2, "0");
  const formatedSeconds = String(timeSeconds % 60).padStart(2, "0");

  return `${formatedMinutes}:${formatedSeconds}`;
}
